import React, { useState } from 'react'
import ManageAccountsContainer from './container'
import { useAddStaffsMutation, useChangeStaffStatusMutation, useDeleteStaffsMutation, useGetStaffsListQuery, useUpdateStaffsMutation } from './services';
import StaffModal from 'components/Modal/StaffModal/StaffModal';
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog';
import { Avatar, Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IOSSwitch from 'components/Fileds/IOSSwitch';
import { manageAccountRoles } from 'utils/constant';

const ManageAccounts = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });

    const [open, setOpen] = useState(false);
    const [editingStaffs, setEditingStaffs] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [delData, setDelData] = useState(false);
    const [togglingStaffId, setTogglingStaffId] = useState(null);
    const [selectedRole, setSelectedRole] = useState('all');

    const { data: resp, isLoading, isError } = useGetStaffsListQuery({
        page: paginationModel.page + 1,        // assuming backend pages are 1-based
        limit: paginationModel.pageSize,
        search: searchQuery,
        role: selectedRole,
    });

    const [addStaffs, { isLoading: adding }] = useAddStaffsMutation();
    const [updateStaffs, { isLoading: updating }] = useUpdateStaffsMutation();
    const [deleteStaffs, { isLoading: isDeleteLoading }] = useDeleteStaffsMutation();
    const [changeStatus, { isLoading: isChangeStatusLoading }] = useChangeStaffStatusMutation();

    // pull out records / total count safely
    const records = resp?.data?.records || [];
    const totalCount = resp?.data?.totalCount || 0;

    const roleLabel = (roleValue) => {
        const match = manageAccountRoles.find(r => r.value === roleValue);
        return match ? match.label : 'N/A';
    };

    // column definitions for CommonTable
    const columns = [
        {
            headerName: 'Staff Name',
            field: 'full_name',
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            renderCell: ({ row }) => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: '100%',
                        paddingLeft: "10px"
                    }}
                >
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: '#000',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        {row?.full_name?.charAt(0)?.toUpperCase() || 'N'}
                    </Avatar>

                    <Box>
                        {row?.full_name || 'N/A'}
                    </Box>
                </Box>
            ),
        },
        {
            headerName: 'Email',
            field: 'email',
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            renderCell: ({ row }) =>
                <Box sx={{ paddingLeft: "10px" }}>
                    {`${row?.email}` || 'N/A'}
                </Box>
        },
        {
            headerName: 'Mobile Number',
            field: 'phone',
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            // renderCell: ({ row }) => `+${row?.country_code} ${row?.phone}` || 'N/A',
            renderCell: ({ row }) =>
                <Box sx={{ paddingLeft: "10px" }}>
                    {`${row?.country_code} ${row?.phone}` || 'N/A'}
                </Box>
        },
        {
            headerName: 'Role',
            field: 'role',
            // flex: 1,
            minWidth: 200,
            maxWidth: 300,
            renderCell: ({ row }) =>
                <Box sx={{ textTransform: 'capitalize' , paddingLeft:"10px"}}>
                    {/* {row?.role || "N/A"} */}
                    {roleLabel(row?.role)}
                </Box>
        },
        {
            headerName: 'Actions',
            field: "action",
            minWidth: 150,
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", alignItems: "center", height: '100%' }}>
                    {/* <IconButton onClick={() => handleEdit(row)} size="small"> */}
                    <EditIcon onClick={() => handleEdit(row)} fontSize="small" sx={{ color: '#1297DE', mr: 1, cursor: 'pointer' }} />
                    {/* </IconButton> */}
                    {/* <IconButton onClick={() => handleDelete(row)} size="small"> */}
                    <DeleteIcon onClick={() => handleDelete(row)} fontSize="small" sx={{ color: '#FF0202', mr: 1, cursor: 'pointer' }} />
                    {/* </IconButton> */}
                    <IOSSwitch checked={row?.active_status} onChange={() => onToggle(row)} disabled={togglingStaffId === row.id} />
                </Box>
            ),
        },
    ];

    const onToggle = async (data) => {
        setTogglingStaffId(data.id);
        try {
            const toggleRes = await changeStatus({
                id: data.id,
                body: !data.active_status
            })
        } catch (err) {
            console.error('Failed to change status', err);
        } finally {
            setTogglingStaffId(null);
        }
    }

    const handleAddBtn = () => {
        setEditingStaffs(null);
        setOpen(true);
    };

    const handleEdit = (staff) => {
        setEditingStaffs(staff);
        setOpen(true);
    };

    const handleDelete = (staff) => {
        setConfirmDialogOpen(true);
        setDelData(staff);
    }


    const handleClose = () => {
        setOpen(false);
        setEditingStaffs(null);
    };

    const handleSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            if (editingStaffs) {
                // await updateStaffs({ id: editingStaffs.id, ...values }).unwrap();
                const updatedValues = {};

                // Only include changed fields
                Object.keys(values).forEach((key) => {
                    if (key == 'password') {
                        if (values?.password) {
                            updatedValues.password = values.password;
                        }
                    } else if (values[key] !== editingStaffs[key]) {
                        updatedValues[key] = values[key];
                    }
                });

                if (Object.keys(updatedValues).length > 0) {
                    await updateStaffs({ id: editingStaffs.id, ...updatedValues }).unwrap();
                }
            } else {
                await addStaffs(values).unwrap();
            }
            handleClose();
        } catch (error) {
            setErrors(error.data?.errors || {});
            console.error('Submit error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            const deleteResponse = await deleteStaffs(delData?.id);
            setConfirmDialogOpen(false);
        } catch (error) {
            console.log(error, "Error in Delete staffs")
        }
    }

    return (
        <>
            <ManageAccountsContainer
                columns={columns}
                rows={records}
                totalCount={totalCount}
                loading={isLoading}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                error={isError}
                handleAddBtn={handleAddBtn}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
            />


            <StaffModal
                open={open}
                onClose={handleClose}
                initialData={editingStaffs}
                onSubmit={handleSubmit}
                loading={adding || updating}
            />

            <ConfirmationDialog
                open={confirmDialogOpen}
                handleClose={() => setConfirmDialogOpen(false)}
                handleConfirm={handleConfirmDelete}
                btnText="Delete"
                infoText={`Are you sure you want to delete this ${delData?.full_name}?`}
                isLoading={isDeleteLoading}
                action={"Delete"}
            />
        </>
    )
}

export default ManageAccounts
